import React, { useState, useMemo, useEffect } from 'react';
import { 
  FileText, Shield, Fish, Award, Compass, 
  Layers, Users, Calendar, AlertCircle, FileCheck, 
  DollarSign, HardHat, GraduationCap, Flame,
  ExternalLink, Mail, Phone, Plus, Trash2, Edit3, 
  Code, Eye, CheckCircle2, ChevronRight, Copy, Check, X
} from 'lucide-react';
import { DBTable } from './DBTable';

/**
 * Converte qualsiasi stringa di data (YYYY-MM-DD, DD/MM/YYYY, ISO, ecc.)
 * nel formato di memorizzazione standard 'aaaa-mm-gg' (YYYY-MM-DD).
 * Questo valore viene utilizzato per il tag React HTML5 <input type="date"> e per il JSONB.
 */
export function normalizeDateToISO(val: any): string {
  if (val === null || val === undefined) return '';
  const str = String(val).trim();
  if (!str) return '';

  // Se è già AAAA-MM-GG (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return str;
  }

  // Se è GG/MM/AAAA o GG-MM-AAAA
  const itMatch = str.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
  if (itMatch) {
    const day = itMatch[1].padStart(2, '0');
    const month = itMatch[2].padStart(2, '0');
    const year = itMatch[3];
    return `${year}-${month}-${day}`;
  }

  // Se contiene ISO string tipo 2026-07-20T...
  const isoPrefixMatch = str.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoPrefixMatch) {
    return isoPrefixMatch[1];
  }

  // Fallback tramite Date parser nativo
  const d = new Date(str);
  if (!isNaN(d.getTime())) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  return str;
}

/**
 * Converte qualsiasi data nel formato visualizzato italiano 'gg/mm/aaaa'.
 */
export function formatDateToIT(val: any): string {
  if (val === null || val === undefined) return '';
  const str = String(val).trim();
  if (!str) return '';

  // Se è AAAA-MM-GG (YYYY-MM-DD)
  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const year = isoMatch[1];
    const month = isoMatch[2];
    const day = isoMatch[3];
    return `${day}/${month}/${year}`;
  }

  // Se è già GG/MM/AAAA
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    return str;
  }

  // Fallback tramite Date parser nativo
  const d = new Date(str);
  if (!isNaN(d.getTime())) {
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  return str;
}

export interface SocioSectionItem {
  key: string;
  label: string;
  data: Record<string, any>;
}

// Struttura di default richiesta per le sezioni
export const DEFAULT_SEZIONI_TEMPLATES: SocioSectionItem[] = [
  {
    key: 'iscrizione',
    label: 'Iscrizione',
    data: {
      data_domanda: '2012-09-01',
      data_verbale_cd: '2026-07-20',
      video_intervista: 'https://youtu.be/kQri3YFpESM?list=RDkQri3YFpESM'
    }
  },
  {
    key: 'decreto_guardia_ecologica',
    label: 'Decreto Guardia Ecologica',
    data: {
      numero_decreto: '208117/08',
      data_decreto: '2007-12-17',
      data_rinnovo: '2024-12-09',
      data_scadenza_decreto: '2026-12-09',
      prefettura_giuramento: 'Bologna',
      data_giuramento: '2008-04-10',
      data_nomina: '2008-05-19',
      prefettura_decreto: 'Bologna',
      competenza_rinnovo_decreto: 'Carabinieri di Budrio',
      email_ufficio_competenza: 'tbo27227@pec.carabinieri.it',
      descrizione_rinnovo_decreto: 'Ultimo atto rinnovo del 21/01/2025',
      link_determina: '',
      link_fotocopia_libretto: ''
    }
  },
  {
    key: 'decreto_guardia_zoofila',
    label: 'Decreto Guardia Zoofila',
    data: {
      numero_decreto: '1083/2021/ggv/area1b',
      data_decreto: '2018-06-28',
      data_rinnovo: '2022-12-10',
      data_scadenza_decreto: '2024-12-10',
      prefettura_decreto: 'Bologna',
      competenza_rinnovo_decreto: '',
      email_ufficio_competenza: '',
      descrizione_rinnovo_decreto: '',
      link_determina: '',
      link_fotocopia_libretto: ''
    }
  },
  {
    key: 'decreto_guardiacaccia',
    label: 'Decreto Guardiacaccia',
    data: {
      numero_decreto: '',
      data_decreto: '',
      data_rinnovo: '',
      data_scadenza_decreto: '',
      prefettura_decreto: '',
      competenza_rinnovo_decreto: '',
      email_ufficio_competenza: '',
      descrizione_rinnovo_decreto: '',
      link_determina: '',
      link_fotocopia_libretto: ''
    }
  },
  {
    key: 'decreto_guardiapesca',
    label: 'Decreto Guardiapesca',
    data: {
      numero_decreto: '',
      data_decreto: '',
      data_rinnovo: '',
      data_scadenza_decreto: '',
      prefettura_decreto: '',
      competenza_rinnovo_decreto: '',
      email_ufficio_competenza: '',
      descrizione_rinnovo_decreto: '',
      link_determina: '',
      link_fotocopia_libretto: ''
    }
  }
];

// Helper per formattare la chiave in camel/title case: '_' separatore di parole con capfirst
export function formatSectionTabTitle(key: string): string {
  if (!key) return '';
  return key
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// Parser flessibile per estrarre le sezioni da attributi_aggiuntivi
export function parseAttributiSezioni(raw: any): SocioSectionItem[] {
  if (!raw) return DEFAULT_SEZIONI_TEMPLATES;
  
  let obj = raw;
  if (typeof raw === 'string') {
    let cleaned = raw.trim();
    if (!cleaned) return DEFAULT_SEZIONI_TEMPLATES;
    // Se c'è sintassi tipo {"sezioni": [ "iscrizione": { ... } ]}, normalizza
    if (cleaned.includes('"sezioni": [')) {
      cleaned = cleaned.replace('"sezioni": [', '"sezioni": {');
      const lastBracket = cleaned.lastIndexOf(']');
      if (lastBracket !== -1) {
        cleaned = cleaned.substring(0, lastBracket) + '}' + cleaned.substring(lastBracket + 1);
      }
    }
    try {
      obj = JSON.parse(cleaned);
    } catch {
      return DEFAULT_SEZIONI_TEMPLATES;
    }
  }

  const sezioni = obj?.sezioni;
  if (!sezioni) {
    // Se non ci sono sezioni esplicite, usiamo i template
    return DEFAULT_SEZIONI_TEMPLATES;
  }

  const normalizeSectionData = (dataObj: any): Record<string, any> => {
    if (typeof dataObj !== 'object' || dataObj === null) return {};
    const res: Record<string, any> = {};
    for (const [k, v] of Object.entries(dataObj)) {
      if (k.startsWith('data_') && v !== null && v !== undefined && v !== '') {
        res[k] = normalizeDateToISO(v);
      } else {
        res[k] = v;
      }
    }
    return res;
  };

  const parsedItems: SocioSectionItem[] = [];

  if (Array.isArray(sezioni)) {
    for (const item of sezioni) {
      if (typeof item === 'object' && item !== null) {
        for (const [k, v] of Object.entries(item)) {
          parsedItems.push({
            key: k,
            label: formatSectionTabTitle(k),
            data: normalizeSectionData(v)
          });
        }
      }
    }
  } else if (typeof sezioni === 'object' && sezioni !== null) {
    for (const [k, v] of Object.entries(sezioni)) {
      parsedItems.push({
        key: k,
        label: formatSectionTabTitle(k),
        data: normalizeSectionData(v)
      });
    }
  }

  if (parsedItems.length === 0) {
    return DEFAULT_SEZIONI_TEMPLATES;
  }

  return parsedItems;
}

// Helper per serializzare le sezioni nel formato richiesto
export function serializeAttributiSezioni(sections: SocioSectionItem[], originalObj: any = {}): any {
  const sezioniArray = sections.map(sec => {
    const cleanData: Record<string, any> = {};
    for (const [k, v] of Object.entries(sec.data || {})) {
      if (k.startsWith('data_')) {
        cleanData[k] = normalizeDateToISO(v);
      } else {
        cleanData[k] = v;
      }
    }
    return {
      [sec.key]: cleanData
    };
  });

  const base = typeof originalObj === 'object' && originalObj !== null ? { ...originalObj } : {};
  base.sezioni = sezioniArray;
  return base;
}

interface SezioniSocioProps {
  socioId?: number | string | null;
  matricola?: number | string | null;
  rawAttributi?: any;
  isEditing?: boolean;
  canWrite?: boolean;
  onChange?: (newAttributi: any) => void;
}

export const SezioniSocio: React.FC<SezioniSocioProps> = ({
  socioId,
  matricola,
  rawAttributi,
  isEditing = false,
  canWrite = false,
  onChange
}) => {
  // Parsing iniziale sezioni
  const [sections, setSections] = useState<SocioSectionItem[]>(() => 
    parseAttributiSezioni(rawAttributi)
  );

  const [activeTab, setActiveTab] = useState<string>('iscrizione');
  const [showJsonRaw, setShowJsonRaw] = useState<boolean>(false);
  const [jsonRawText, setJsonRawText] = useState<string>('');
  const [jsonError, setJsonError] = useState<string>('');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [isAddingField, setIsAddingField] = useState<boolean>(false);
  const [newFieldKey, setNewFieldKey] = useState<string>('');

  // Aggiorna sections se cambiano i props esterni rawAttributi
  useEffect(() => {
    const parsed = parseAttributiSezioni(rawAttributi);
    setSections(parsed);
    setJsonRawText(JSON.stringify(serializeAttributiSezioni(parsed, rawAttributi), null, 2));
  }, [rawAttributi]);

  // Tab delle griglie DB successive alle sezioni degli attributi
  const dbGridTabs = useMemo(() => [
    {
      id: 'gruppi',
      label: 'Gruppi',
      tableName: 'cpgev_socio_gruppi',
      icon: Users,
      columns: ['id', 'gruppo', 'ruolo', 'data_inizio', 'data_fine', 'note'],
      columnLabels: {
        id: 'ID',
        gruppo: 'Gruppo',
        ruolo: 'Ruolo',
        data_inizio: 'Data Inizio',
        data_fine: 'Data Fine',
        note: 'Note'
      }
    },
    {
      id: 'servizi',
      label: 'Servizi',
      tableName: 'cpgev_socio_servizi',
      icon: Layers,
      columns: ['id', 'codice_servizio', 'descrizione', 'data_servizio', 'ore', 'luogo', 'stato', 'note'],
      columnLabels: {
        id: 'ID',
        codice_servizio: 'Cod. Servizio',
        descrizione: 'Descrizione',
        data_servizio: 'Data Servizio',
        ore: 'Ore',
        luogo: 'Luogo',
        stato: 'Stato',
        note: 'Note'
      }
    },
    {
      id: 'verbali_ispezione',
      label: 'Verb. ispezione',
      tableName: 'cpgev_socio_verbali_ispezione',
      icon: FileCheck,
      columns: ['id', 'numero_verbale', 'data_verbale', 'localita', 'esito', 'agenti', 'note'],
      columnLabels: {
        id: 'ID',
        numero_verbale: 'N. Verbale',
        data_verbale: 'Data Verbale',
        localita: 'Località',
        esito: 'Esito',
        agenti: 'Agenti',
        note: 'Note'
      }
    },
    {
      id: 'verbali_sanzionatori',
      label: 'Verb. sanzionatori',
      tableName: 'cpgev_socio_verbali_sanzionatori',
      icon: AlertCircle,
      columns: ['id', 'numero_verbale', 'data_verbale', 'trasgressore', 'norma_violata', 'importo', 'stato_pagamento', 'note'],
      columnLabels: {
        id: 'ID',
        numero_verbale: 'N. Verbale',
        data_verbale: 'Data',
        trasgressore: 'Trasgressore',
        norma_violata: 'Norma Violata',
        importo: 'Importo (€)',
        stato_pagamento: 'Stato Pagamento',
        note: 'Note'
      }
    },
    {
      id: 'rimborsi_km',
      label: 'Rimborsi km',
      tableName: 'cpgev_socio_rimborsi_chilometrici',
      icon: DollarSign,
      columns: ['id', 'data_missione', 'destinazione', 'km_percorsi', 'importo_km', 'totale_rimborso', 'stato', 'note'],
      columnLabels: {
        id: 'ID',
        data_missione: 'Data Missione',
        destinazione: 'Destinazione',
        km_percorsi: 'Km Percorsi',
        importo_km: 'Tariffa/Km (€)',
        totale_rimborso: 'Totale (€)',
        stato: 'Stato',
        note: 'Note'
      }
    },
    {
      id: 'rimborsi_spese',
      label: 'Rimborsi spese',
      tableName: 'cpgev_socio_rimborsi_spese',
      icon: DollarSign,
      columns: ['id', 'data_spesa', 'tipologia_spesa', 'descrizione', 'importo', 'giustificativo', 'stato', 'note'],
      columnLabels: {
        id: 'ID',
        data_spesa: 'Data Spesa',
        tipologia_spesa: 'Tipologia',
        descrizione: 'Descrizione',
        importo: 'Importo (€)',
        giustificativo: 'N. Giustificativo',
        stato: 'Stato',
        note: 'Note'
      }
    },
    {
      id: 'dpi',
      label: 'DPI',
      tableName: 'cpgev_socio_dpi',
      icon: HardHat,
      columns: ['id', 'articolo', 'taglia', 'data_consegna', 'data_restituzione', 'stato_usura', 'note'],
      columnLabels: {
        id: 'ID',
        articolo: 'Dispositivo / DPI',
        taglia: 'Taglia',
        data_consegna: 'Data Consegna',
        data_restituzione: 'Data Restituzione',
        stato_usura: 'Stato Usura',
        note: 'Note'
      }
    },
    {
      id: 'corsi',
      label: 'Corsi',
      tableName: 'cpgev_socio_corsi',
      icon: GraduationCap,
      columns: ['id', 'titolo_corso', 'ente_formatore', 'data_inizio', 'data_fine', 'ore_totali', 'attestato', 'note'],
      columnLabels: {
        id: 'ID',
        titolo_corso: 'Titolo Corso',
        ente_formatore: 'Ente Formatore',
        data_inizio: 'Data Inizio',
        data_fine: 'Data Fine',
        ore_totali: 'Ore Totali',
        attestato: 'Attestato',
        note: 'Note'
      }
    },
    {
      id: 'esercitazioni',
      label: 'Esercitazioni',
      tableName: 'cpgev_socio_esercitazioni',
      icon: Flame,
      columns: ['id', 'nome_esercitazione', 'luogo', 'data_svolgimento', 'ruolo_svolto', 'durata_ore', 'note'],
      columnLabels: {
        id: 'ID',
        nome_esercitazione: 'Nome Esercitazione',
        luogo: 'Luogo',
        data_svolgimento: 'Data Svolgimento',
        ruolo_svolto: 'Ruolo Svolto',
        durata_ore: 'Durata (Ore)',
        note: 'Note'
      }
    }
  ], []);

  // Icona tematica per le sezioni
  const getSectionIcon = (key: string) => {
    if (key.includes('pesca')) return Fish;
    if (key.includes('caccia')) return Compass;
    if (key.includes('ecolog') || key.includes('zoofil')) return Shield;
    if (key.includes('iscr')) return FileText;
    return Award;
  };

  // Aggiornamento di un campo nella sezione attiva
  const handleFieldChange = (sectionKey: string, fieldKey: string, value: any) => {
    let normalizedVal = value;
    if (fieldKey.startsWith('data_')) {
      normalizedVal = normalizeDateToISO(value);
    }

    const updated = sections.map(sec => {
      if (sec.key === sectionKey) {
        return {
          ...sec,
          data: {
            ...sec.data,
            [fieldKey]: normalizedVal
          }
        };
      }
      return sec;
    });
    setSections(updated);
    const serialized = serializeAttributiSezioni(updated, rawAttributi);
    setJsonRawText(JSON.stringify(serialized, null, 2));
    if (onChange) onChange(serialized);
  };

  // Gestione modifica diretta JSON
  const handleJsonRawChange = (text: string) => {
    setJsonRawText(text);
    try {
      const parsed = JSON.parse(text);
      setJsonError('');
      const extracted = parseAttributiSezioni(parsed);
      setSections(extracted);
      if (onChange) onChange(parsed);
    } catch (err: any) {
      setJsonError(err.message || 'JSON non valido');
    }
  };

  // Aggiunge un nuovo campo alla sezione attiva
  const handleAddField = (sectionKey: string, fieldKeyRaw: string) => {
    const fieldKey = fieldKeyRaw.trim().toLowerCase().replace(/\s+/g, '_');
    if (!fieldKey) return;

    const updated = sections.map(sec => {
      if (sec.key === sectionKey) {
        return {
          ...sec,
          data: {
            ...sec.data,
            [fieldKey]: ''
          }
        };
      }
      return sec;
    });

    setSections(updated);
    const serialized = serializeAttributiSezioni(updated, rawAttributi);
    setJsonRawText(JSON.stringify(serialized, null, 2));
    if (onChange) onChange(serialized);
    setNewFieldKey('');
    setIsAddingField(false);
  };

  // Elimina un campo dalla sezione attiva
  const handleDeleteField = (sectionKey: string, fieldKey: string) => {
    const updated = sections.map(sec => {
      if (sec.key === sectionKey) {
        const copy = { ...sec.data };
        delete copy[fieldKey];
        return {
          ...sec,
          data: copy
        };
      }
      return sec;
    });

    setSections(updated);
    const serialized = serializeAttributiSezioni(updated, rawAttributi);
    setJsonRawText(JSON.stringify(serialized, null, 2));
    if (onChange) onChange(serialized);
  };

  // Trova la sezione attiva corrente se è una sezione attributi
  const currentSection = sections.find(s => s.key === activeTab);
  const currentDbGrid = dbGridTabs.find(g => g.id === activeTab);

  // Se la tab attiva non esiste più, seleziona la prima
  useEffect(() => {
    const exists = sections.some(s => s.key === activeTab) || dbGridTabs.some(g => g.id === activeTab);
    if (!exists) {
      setActiveTab(sections[0]?.key || 'iscrizione');
    }
  }, [sections, dbGridTabs, activeTab]);

  return (
    <div id="container-sezioni-socio" className="bg-white rounded-2xl border border-slate-200/90 shadow-2xs overflow-hidden">
      {/* Header Sezioni */}
      <div className="px-5 py-4 border-b border-slate-200/80 bg-slate-50/70 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-300 flex items-center justify-center text-amber-700">
            <Layers className="w-4 h-4" />
          </div>
          <div>
            <h4 className="text-sm font-black text-slate-800 tracking-tight">
              Sezioni
            </h4>
            <p className="text-[11px] font-medium text-slate-400">
              {isEditing ? 'Configurazione campi sezioni e registri socio' : 'Decreti, abilitazioni e registri operativi del socio'}
            </p>
          </div>
        </div>

        {/* Pulsante JSON grezzo in edit mode */}
        {isEditing && (
          <button
            type="button"
            onClick={() => setShowJsonRaw(!showJsonRaw)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
              showJsonRaw 
                ? 'bg-amber-600 text-white shadow-xs' 
                : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-300'
            }`}
          >
            <Code className="w-3.5 h-3.5" />
            <span>{showJsonRaw ? 'Visualizza Schede' : 'Editor JSON Grezzo'}</span>
          </button>
        )}
      </div>

      {/* Editor JSON Grezzo (Alternativo per utenti tecnici) */}
      {isEditing && showJsonRaw ? (
        <div className="p-5 space-y-3 bg-slate-900 text-slate-100">
          <div className="flex items-center justify-between text-xs font-mono text-slate-400">
            <span>Struttura `attributi_aggiuntivi.sezioni`</span>
            {jsonError ? (
              <span className="text-red-400 font-bold flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> {jsonError}
              </span>
            ) : (
              <span className="text-emerald-400 font-bold flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> JSON Valido
              </span>
            )}
          </div>
          <textarea
            rows={14}
            value={jsonRawText}
            onChange={(e) => handleJsonRawChange(e.target.value)}
            className="w-full p-4 font-mono text-xs bg-slate-950 border border-slate-800 rounded-xl text-amber-300 outline-none focus:ring-2 focus:ring-amber-500 transition-all resize-y"
          />
        </div>
      ) : (
        <>
          {/* BARRA DELLE TABS */}
          <div className="border-b border-slate-200/80 bg-slate-100/60 px-3 pt-2">
            <div className="flex items-center gap-1 overflow-x-auto no-scrollbar scroll-smooth">
              {/* TABS SEZIONI ATTRIBUTI (JSONB) */}
              {sections.map((sec) => {
                const IconComp = getSectionIcon(sec.key);
                const isActive = activeTab === sec.key;
                return (
                  <button
                    key={sec.key}
                    type="button"
                    onClick={() => setActiveTab(sec.key)}
                    className={`px-3.5 py-2.5 rounded-t-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 border-t-2 border-x border-b -mb-px shrink-0 ${
                      isActive
                        ? 'bg-white border-t-amber-600 border-x-slate-200 border-b-white text-amber-700 shadow-2xs z-10'
                        : 'bg-transparent border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-200/50'
                    }`}
                  >
                    <IconComp className={`w-3.5 h-3.5 ${isActive ? 'text-amber-600' : 'text-slate-400'}`} />
                    <span>{sec.label}</span>
                  </button>
                );
              })}

              <div className="h-5 w-px bg-slate-300 mx-1 shrink-0" />

              {/* TABS GRIGLIE DATABASE */}
              {dbGridTabs.map((grid) => {
                const IconComp = grid.icon;
                const isActive = activeTab === grid.id;
                return (
                  <button
                    key={grid.id}
                    type="button"
                    onClick={() => setActiveTab(grid.id)}
                    className={`px-3.5 py-2.5 rounded-t-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 border-t-2 border-x border-b -mb-px shrink-0 ${
                      isActive
                        ? 'bg-white border-t-indigo-600 border-x-slate-200 border-b-white text-indigo-700 shadow-2xs z-10'
                        : 'bg-transparent border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-200/50'
                    }`}
                  >
                    <IconComp className={`w-3.5 h-3.5 ${isActive ? 'text-indigo-600' : 'text-slate-400'}`} />
                    <span>{grid.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* CONTENUTO DEL TAB ATTIVO */}
          <div className="p-5 sm:p-6">
            {/* 1. SEZIONE ATTRIBUTI JSONB ATTIVA */}
            {currentSection && (
              <div id={`tab-content-${currentSection.key}`} className="space-y-5">
                <div className="flex items-center justify-between pb-3 border-b border-slate-100 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-black uppercase tracking-wider text-slate-800">
                      {currentSection.label}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-amber-50 text-amber-800 border border-amber-200">
                      key: {currentSection.key}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-400">
                      {Object.keys(currentSection.data || {}).length} campi definiti
                    </span>
                    {isEditing && !isAddingField && (
                      <button
                        type="button"
                        onClick={() => setIsAddingField(true)}
                        className="px-2.5 py-1 text-xs font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg flex items-center gap-1 transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Aggiungi Campo
                      </button>
                    )}
                  </div>
                </div>

                {isEditing && isAddingField && (
                  <div className="p-3.5 bg-amber-50/70 border border-amber-200/90 rounded-xl space-y-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-amber-900 flex items-center gap-1.5">
                        <Plus className="w-3.5 h-3.5 text-amber-600" />
                        Nuovo attributo per la sezione "{currentSection.label}"
                      </span>
                      <button
                        type="button"
                        onClick={() => { setIsAddingField(false); setNewFieldKey(''); }}
                        className="text-slate-400 hover:text-slate-600 p-1"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <input
                        type="text"
                        value={newFieldKey}
                        onChange={(e) => setNewFieldKey(e.target.value)}
                        placeholder="Nome attributo (es. data_visita_medica, link_documento)"
                        className="flex-1 min-w-[240px] px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-mono text-slate-800 focus:outline-none focus:border-amber-500"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddField(currentSection.key, newFieldKey);
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => handleAddField(currentSection.key, newFieldKey)}
                        disabled={!newFieldKey.trim()}
                        className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-colors"
                      >
                        Crea Campo
                      </button>
                      <button
                        type="button"
                        onClick={() => { setIsAddingField(false); setNewFieldKey(''); }}
                        className="px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-semibold transition-colors"
                      >
                        Annulla
                      </button>
                    </div>
                    {newFieldKey.trim().toLowerCase().startsWith('data_') && (
                      <p className="text-[11px] text-amber-800 font-medium flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5 text-amber-600" />
                        Prefisso <strong>data_</strong> rilevato: verrà configurato come campo Data interattivo (wizard data, visualizzato <code>gg/mm/aaaa</code>, memorizzato <code>aaaa-mm-gg</code>).
                      </p>
                    )}
                  </div>
                )}

                {isEditing ? (
                  /* MODIFICA CAMPI DELLA SEZIONE */
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(currentSection.data || {}).map(([fKey, fVal]) => {
                      const fieldLabel = formatSectionTabTitle(fKey);
                      const isDate = fKey.startsWith('data_');
                      const isLink = fKey.includes('link_') || fKey.includes('video_');
                      const isEmail = fKey.includes('email_');
                      const isLongText = fKey.includes('descrizione_') || fKey.includes('note');

                      return (
                        <div key={fKey} className={isLongText ? 'sm:col-span-2 lg:col-span-3' : ''}>
                          <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center justify-between">
                            <span className="truncate flex items-center gap-1.5">
                              {isDate && <Calendar className="w-3.5 h-3.5 text-amber-600 shrink-0" />}
                              <span>{fieldLabel}</span>
                            </span>
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] font-mono text-slate-400">{fKey}</span>
                              <button
                                type="button"
                                onClick={() => handleDeleteField(currentSection.key, fKey)}
                                title={`Elimina campo ${fKey}`}
                                className="text-slate-300 hover:text-red-500 p-0.5 rounded transition-colors"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </label>

                          {isLongText ? (
                            <textarea
                              rows={2}
                              value={fVal || ''}
                              onChange={(e) => handleFieldChange(currentSection.key, fKey, e.target.value)}
                              placeholder={`Inserisci ${fieldLabel}...`}
                              className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-medium text-slate-900 focus:bg-white focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                            />
                          ) : isDate ? (
                            <div className="space-y-1">
                              <div className="relative flex items-center">
                                <input
                                  type="date"
                                  value={normalizeDateToISO(fVal)}
                                  onChange={(e) => handleFieldChange(currentSection.key, fKey, e.target.value)}
                                  onClick={(e) => {
                                    try {
                                      (e.currentTarget as any).showPicker?.();
                                    } catch {}
                                  }}
                                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold text-slate-900 focus:bg-white focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all cursor-pointer"
                                />
                                {fVal && (
                                  <button
                                    type="button"
                                    onClick={() => handleFieldChange(currentSection.key, fKey, '')}
                                    title="Cancella data"
                                    className="absolute right-8 p-1 text-slate-400 hover:text-red-500 rounded-md transition-colors"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                              <div className="flex items-center justify-between px-1 text-[10px] text-slate-500 font-medium">
                                <span>Visualizzata: <strong className="text-slate-700 font-mono">{formatDateToIT(fVal) || '-'}</strong></span>
                                <span>Memorizzata: <strong className="text-amber-800 font-mono">{normalizeDateToISO(fVal) || '-'}</strong></span>
                              </div>
                            </div>
                          ) : (
                            <div className="relative flex items-center">
                              <input
                                type={isEmail ? 'email' : 'text'}
                                value={fVal || ''}
                                onChange={(e) => handleFieldChange(currentSection.key, fKey, e.target.value)}
                                placeholder={isLink ? 'https://...' : `Valore ${fieldLabel}...`}
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-medium text-slate-900 focus:bg-white focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all pr-8"
                              />
                              {isLink && fVal && (
                                <a
                                  href={fVal}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="absolute right-2 text-slate-400 hover:text-amber-600 p-1"
                                  title="Testa link"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* VISUALIZZAZIONE SCHEDA DELLA SEZIONE */
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                    {Object.entries(currentSection.data || {}).map(([fKey, fVal]) => {
                      const fieldLabel = formatSectionTabTitle(fKey);
                      const isDate = fKey.startsWith('data_');
                      const isLink = Boolean(fVal && (fKey.includes('link_') || fKey.includes('video_') || String(fVal).startsWith('http')));
                      const isEmail = Boolean(fVal && (fKey.includes('email') || String(fVal).includes('@')));
                      const isLongText = fKey.includes('descrizione_') || fKey.includes('note');
                      const isEmpty = fVal === '' || fVal === null || fVal === undefined;

                      return (
                        <div 
                          key={fKey} 
                          className={`p-3.5 rounded-xl border border-slate-200/80 bg-slate-50/50 hover:bg-slate-50 transition-all ${
                            isLongText ? 'sm:col-span-2 lg:col-span-3' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between gap-1 mb-1">
                            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 truncate flex items-center gap-1">
                              {isDate && <Calendar className="w-3 h-3 text-amber-600" />}
                              <span>{fieldLabel}</span>
                            </span>
                            <span className="text-[9px] font-mono text-slate-400 shrink-0">
                              {fKey}
                            </span>
                          </div>

                          <div className="mt-0.5">
                            {isEmpty ? (
                              <span className="text-xs font-medium text-slate-400 italic">
                                Non specificato
                              </span>
                            ) : isDate ? (
                              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                                <Calendar className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                                <span className="font-mono tracking-wide">{formatDateToIT(fVal)}</span>
                              </div>
                            ) : isLink ? (
                              <div className="flex items-center gap-2">
                                <a
                                  href={String(fVal)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-xs font-bold text-amber-700 hover:text-amber-800 underline underline-offset-2 flex items-center gap-1.5 truncate max-w-full group"
                                >
                                  <ExternalLink className="w-3.5 h-3.5 shrink-0 text-amber-600 group-hover:scale-110 transition-transform" />
                                  <span className="truncate">{String(fVal)}</span>
                                </a>
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(String(fVal));
                                    setCopiedKey(fKey);
                                    setTimeout(() => setCopiedKey(null), 1500);
                                  }}
                                  className="text-slate-400 hover:text-slate-600 p-1 shrink-0"
                                  title="Copia link"
                                >
                                  {copiedKey === fKey ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                                </button>
                              </div>
                            ) : isEmail ? (
                              <a
                                href={`mailto:${fVal}`}
                                className="text-xs font-bold text-indigo-700 hover:text-indigo-900 flex items-center gap-1.5 truncate"
                              >
                                <Mail className="w-3.5 h-3.5 shrink-0 text-indigo-600" />
                                <span className="truncate">{String(fVal)}</span>
                              </a>
                            ) : (
                              <p className="text-xs font-bold text-slate-800 select-all break-words">
                                {String(fVal)}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* 2. GRIGLIA DATABASE ATTIVA (DBTable) */}
            {currentDbGrid && (
              <div id={`tab-content-${currentDbGrid.id}`} className="space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-slate-100 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-black text-slate-800">
                      {currentDbGrid.label}
                    </span>
                    <span className="text-xs text-slate-400">
                      Tabella: <code className="font-mono text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">{currentDbGrid.tableName}</code>
                    </span>
                  </div>
                  {socioId && (
                    <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-lg">
                      Filtro Socio ID: <strong className="text-slate-800 font-mono">#{socioId}</strong>
                    </span>
                  )}
                </div>

                <div className="border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
                  <DBTable
                    title={currentDbGrid.label}
                    tableName={currentDbGrid.tableName}
                    schema="public"
                    whereClause={socioId ? `id = ${socioId}` : undefined}
                    columns={currentDbGrid.columns}
                    columnLabels={currentDbGrid.columnLabels}
                    sortable={true}
                    searchable={true}
                    searchPlaceholder={`Cerca in ${currentDbGrid.label}...`}
                    filterable={true}
                    selectable={true}
                    limit={100}
                    pageSize={20}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
